import React from 'react';
import { ThemeProvider } from 'styled-components';
import { StyledOrnament } from './styles';
import Icon from '../Icon/Icon';
import { FeedbackVariants, OrnamentVariantColorTypes } from '../_common/Types/GlobalTypes';
import { IconTypes } from '../../helpers/constants/icons';

/*
  These props are marked as @ignore because when the component is export
  it shouldn't expose these props in the documentation
*/

export interface OrnamentProps {
  /** @ignore - Modifier for icon type, should not be exposed once this component is exported */
  icon?: IconTypes;
  /** @ignore - Modifier for background color, should not be exposed once this component is exported */
  color?: OrnamentVariantColorTypes;
  /** Modifier for Ornament size */
  size?: 'small' | 'medium' | 'large';
  /** @ignore - Modifier for spinning ornaments, should not be exposed once this component is exported */
  isSpinner?: boolean;
  /** @ignore - Modifier for inverse ornaments, should not be exposed once this component is exported */
  isInverse?: boolean;
  /** @ignore- Modifier for dashed ornaments, should not be exposed once this component is exported */
  isDashed?: boolean;
  /** Modifier for both icon and background color */
  variant?: Extract<
    FeedbackVariants,
    | 'feedback-positive'
    | 'feedback-negative'
    | 'feedback-notice'
    | 'feedback-neutral'
    | 'feedback-pending'
    | 'feedback-informational'
    | 'feedback-staff'
    | 'feedback-loading'
  >;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

/*
    Notes:

    1. Our semantic variants use custom icons, so we are
    temporarily using custom svgs, that are located inside
    the /components folder.

    2. In the future, we will need to update `checkmark`,
    `close`, `loading`, `right-arrow` and `more-horizontal`
    with these new, improved icons, because currently they
    are duplicated between the icons in ./components and
    the ink icon library.

    3. These should be tokenized once we update our icon
    library with these svgs.
  */
const variantIcons: {
  [key in Extract<
    FeedbackVariants,
    | 'feedback-positive'
    | 'feedback-negative'
    | 'feedback-notice'
    | 'feedback-neutral'
    | 'feedback-pending'
    | 'feedback-informational'
    | 'feedback-staff'
    | 'feedback-loading'
  >]: 'checkmark' | 'close' | 'loading' | 'warning' | 'arrow' | 'users' | 'informational';
} = {
  'feedback-neutral': 'checkmark',
  'feedback-positive': 'checkmark',
  'feedback-negative': 'close',
  'feedback-pending': 'loading',
  'feedback-notice': 'warning',
  'feedback-informational': 'informational',
  'feedback-staff': 'users',
  'feedback-loading': 'loading',
} as const;

const getSemanticVariant = (variant: OrnamentProps['variant']) => variant?.replace(/feedback-/g, '');

const iconSizeMap = {
  small: 'lg',
  medium: 'xxl',
  large: 'xxl',
} as const;

function Ornament(props: OrnamentProps) {
  const { icon, color, size, variant, isDashed, isInverse, isSpinner, ...otherProps } = props;

  const fixedVariant = variant?.startsWith('feedback-') ? variant : `feedback-${variant}`;

  if (variant && (color || icon || isDashed || isInverse)) {
    throw new Error(
      `[_ink] 'color', 'icon', 'isDashed', and 'isInverse' should not be used with 'variant' in Ornament`,
    );
  }

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        ornamentColor: (variant && fixedVariant) || color,
        ornamentSize: size,
        ornamentInverse: variant ? false : isInverse,
        ornamentDashed: variant ? false : isDashed,
        ornamentIcon: icon,
        ornamentVariant: variant && fixedVariant,
      })}
    >
      <StyledOrnament {...otherProps} data-test-variant={getSemanticVariant(variant)}>
        <Icon
          type={variant === undefined ? icon : variantIcons[fixedVariant]}
          isSpinner={variant === 'feedback-loading' || isSpinner}
          size={size === undefined ? iconSizeMap.large : iconSizeMap[size]}
          aria-hidden="true"
        />
      </StyledOrnament>
    </ThemeProvider>
  );
}

Ornament.defaultProps = {
  size: 'large',
  isSpinner: false,
  isInverse: false,
  isDashed: false,
};

Ornament.displayName = 'Ornament';

export default Ornament;
